import Button from "components/Button";
import Title from "components/Title";
import Checkbox from "components/Checkbox";
import React, { useCallback, useEffect, useState } from "react";
import api from "../../api";
import { tryCatch } from "../../utils";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import PageLayout from "../../layouts/PageLayout";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import OnboardingSteps from "./components/OnboardingSteps";

const styles = {
  content: {
    width: "100%",
    maxWidth: "92%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  body: {
    fontSize: "15px",
    color: "rgba(26,26,26,0.88)",
    lineHeight: 1.55,
    marginTop: 0,
    marginBottom: "12px",
  },
  bodyTight: {
    fontSize: "15px",
    color: "rgba(26,26,26,0.88)",
    lineHeight: 1.55,
    marginTop: 0,
    marginBottom: "8px",
  },
  nordSection: {
    marginTop: "24px",
    marginBottom: "24px",
    padding: "16px 18px",
    borderRadius: "8px",
    border: "1px solid rgba(26,26,26,0.12)",
    backgroundColor: "rgba(26,26,26,0.03)",
  },
  nordHeadline: {
    fontSize: "15px",
    fontWeight: 600,
    color: "rgba(26,26,26,0.95)",
    marginTop: 0,
    marginBottom: "8px",
  },
  nordSubtext: {
    fontSize: "14px",
    color: "rgba(26,26,26,0.78)",
    lineHeight: 1.5,
    marginTop: 0,
    marginBottom: "14px",
  },
  claimButtons: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    alignItems: "stretch" as const,
  },
  checkboxWrap: {
    marginTop: "4px",
    marginBottom: "20px",
  },
  dashboardBlock: {
    marginTop: "8px",
  },
  dashboardHint: {
    fontSize: "14px",
    color: "rgba(26,26,26,0.78)",
    lineHeight: 1.5,
    marginTop: 0,
    marginBottom: "14px",
  },
};

const AllSetContainer: React.FC = () => {
  const { t } = useTranslation("onboarding");
  const history = useHistory();
  const { adapter } = useSelector((state: RootState) => ({
    adapter: state.adapter.adapter!,
  }));
  const [unclaimedCodeTypes, setUnclaimedCodeTypes] = useState<string[]>([]);
  const [newsletterToggle, setNewsletterToggle] = useState<boolean>(false);

  const getNewCodeList = useCallback(async () => {
    let codeListRes = await tryCatch(api.subscription.getCodesList());
    if (codeListRes.error) {
      console.log("Error fetching codes: ", codeListRes.error);
    } else {
      if (
        codeListRes.result &&
        codeListRes.result.data &&
        codeListRes.result.data.unclaimed_codes
      ) {
        codeListRes.result.data.unclaimed_codes.forEach((unclaimed_codes) => {
          if (unclaimed_codes.adapter_id === adapter.id) {
            setUnclaimedCodeTypes(unclaimed_codes.code_types);
          }
        });
      }
    }
  }, [adapter]);

  useEffect(() => {
    getNewCodeList();
  }, [getNewCodeList]);

  const handleClaimCode = async (type: string) => {
    const apiResult = await tryCatch(
      api.subscription.claimCode(adapter.id, type)
    );

    if (apiResult.error) {
      console.log("Error claiming code: ", apiResult.error.message);
    } else {
      if (apiResult.result.data.result === "Code claimed") {
        window.location.href = "/account?reason=code_claimed";
      } else {
        console.log("Error claiming code: ", apiResult.result.data.result);
      }
    }
  };

  const handleGoToDashboard = async () => {
    if (newsletterToggle) {
      const apiResult = await tryCatch(api.account.setNewsletterFlag(true));
      if (apiResult.error) {
        console.log(
          "Error updating newsletter flag: ",
          apiResult.error.message
        );
      }
    }
    history.push("/account");
  };

  return (
    <PageLayout>
      <OnboardingSteps stage="allSet" />
      <div style={styles.content}>
        <Title title={t("allSet.title")} className="mb-4" />
        <p style={styles.body}>{t("allSet.lineActive")}</p>
        <p style={styles.bodyTight}>{t("allSet.lineIncluded")}</p>
        <p style={{ ...styles.body, marginBottom: 0 }}>
          {t("allSet.linePlanBegins")}
        </p>

        {unclaimedCodeTypes.length > 0 && (
          <div style={styles.nordSection}>
            <p style={styles.nordHeadline}>{t("allSet.nordHeadline")}</p>
            <p style={styles.nordSubtext}>{t("allSet.nordSubtext")}</p>
            <div style={styles.claimButtons}>
              {unclaimedCodeTypes.map((type) => (
                <Button
                  key={type}
                  type="button"
                  onClick={() => {
                    handleClaimCode(type);
                  }}
                >
                  {t("allSet.claimNordCodeButton", { codeType: type })}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div style={styles.checkboxWrap}>
          <Checkbox
            id="newsletterToggle"
            checked={newsletterToggle}
            change={() => {
              setNewsletterToggle(!newsletterToggle);
            }}
            label={t("allSet.newsletterMessage")}
          />
        </div>

        <div style={styles.dashboardBlock}>
          <p style={styles.dashboardHint}>{t("allSet.dashboardHint")}</p>
          <Button type="button" onClick={handleGoToDashboard}>
            {t("allSet.goToDashboard")}
          </Button>
        </div>
      </div>
    </PageLayout>
  );
};
export default AllSetContainer;
